import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";
import useAdminEssays from "@/pages/admin/essays/useAdminEssays";
import useAdminReferences from "@/pages/admin/references/useAdminReferences";
import { clearCache } from "@/pages/admin/adminCache";

const KEY_STORAGE = "vbb_admin_key";

/**
 * /admin/essays/new + /admin/essays/:id
 * Single form component reused for creation + editing. On creation
 * the URL is `/admin/essays/new`; on save we navigate to `/admin/essays/:id`.
 */
export default function AdminEssayEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { key, createEssay, getEssay, patchEssay } = useAdminEssays();
  const { references } = useAdminReferences();

  const [loaded, setLoaded] = useState(isNew);
  const [savedState, setSavedState] = useState("idle"); // idle|saving|saved|error
  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    body: "",
    reference_ids: [],
    published: false,
  });

  useEffect(() => {
    if (!key) {
      navigate("/login?next=/admin/essays", { replace: true });
      return;
    }
    if (isNew) return;
    let cancelled = false;
    getEssay(id)
      .then((data) => {
        if (cancelled) return;
        setForm({
          title: data.title || "",
          slug: data.slug || "",
          excerpt: data.excerpt || "",
          body: data.body || "",
          reference_ids: data.reference_ids || [],
          published: !!data.published,
        });
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setSavedState("error");
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, key, getEssay, navigate]);

  const onLock = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    navigate("/login?next=/admin/essays", { replace: true });
  };

  const onChange = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleReference = (refId) =>
    setForm((f) => ({
      ...f,
      reference_ids: f.reference_ids.includes(refId)
        ? f.reference_ids.filter((x) => x !== refId)
        : [...f.reference_ids, refId],
    }));

  const onSave = async () => {
    if (!form.title.trim()) {
      setSavedState("error");
      return;
    }
    setSavedState("saving");
    try {
      if (isNew) {
        const created = await createEssay(form);
        setSavedState("saved");
        navigate(`/admin/essays/${created.id}`, { replace: true });
        return;
      }
      await patchEssay(id, form);
      setSavedState("saved");
      setTimeout(() => setSavedState("idle"), 1800);
    } catch {
      setSavedState("error");
    }
  };

  if (!key || !loaded) return null;

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="admin-essay-edit-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">
            <Link to="/admin" className="vbb-link" data-testid="crumb-admin">
              Admin
            </Link>{" "}
            /{" "}
            <Link
              to="/admin/essays"
              className="vbb-link"
              data-testid="crumb-essays"
            >
              Essays
            </Link>{" "}
            / {isNew ? "New" : "Edit"}
          </div>
          <h1
            className="font-display uppercase text-4xl sm:text-5xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="admin-essay-edit-title"
          >
            <BoxedTitle size="lg" thick>
              {isNew ? "New" : "Edit"}
            </BoxedTitle>{" "}
            essay.
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/essays"
            className="vbb-btn vbb-btn-ghost"
            data-testid="essay-back-btn"
          >
            ← Essays
          </Link>
          <button
            onClick={onLock}
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-lock-btn"
          >
            Lock
          </button>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <div>
            <label
              htmlFor="essay-title"
              className="vbb-overline block mb-2"
            >
              Title
            </label>
            <input
              id="essay-title"
              type="text"
              value={form.title}
              onChange={(e) => onChange({ title: e.target.value })}
              placeholder="Working title — you can rename anytime"
              className="w-full border border-[#0a0a0a] p-3 text-[18px] focus:outline-none focus:border-[#e34234]"
              data-testid="essay-title-input"
            />
          </div>

          <div>
            <label
              htmlFor="essay-slug"
              className="vbb-overline block mb-2"
            >
              Slug (auto-generated on save if blank)
            </label>
            <input
              id="essay-slug"
              type="text"
              value={form.slug}
              onChange={(e) => onChange({ slug: e.target.value })}
              placeholder="my-essay-slug"
              className="w-full border border-[#0a0a0a] p-3 font-mono-tiny text-[13px] focus:outline-none focus:border-[#e34234]"
              data-testid="essay-slug-input"
            />
          </div>

          <div>
            <label
              htmlFor="essay-excerpt"
              className="vbb-overline block mb-2"
            >
              Excerpt (1-2 sentences for the index)
            </label>
            <textarea
              id="essay-excerpt"
              value={form.excerpt}
              onChange={(e) => onChange({ excerpt: e.target.value })}
              rows={3}
              className="w-full border border-[#0a0a0a] p-3 text-[14px] resize-y focus:outline-none focus:border-[#e34234]"
              data-testid="essay-excerpt-input"
            />
          </div>

          <div>
            <label
              htmlFor="essay-body"
              className="vbb-overline block mb-2"
            >
              Body (markdown)
            </label>
            <textarea
              id="essay-body"
              value={form.body}
              onChange={(e) => onChange({ body: e.target.value })}
              rows={20}
              className="w-full border border-[#0a0a0a] p-4 font-mono-tiny text-[13px] resize-y focus:outline-none focus:border-[#e34234]"
              placeholder="# Section&#10;&#10;Write in markdown…"
              data-testid="essay-body-input"
            />
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <section
            className="border border-[#0a0a0a] p-5"
            data-testid="essay-publish-panel"
          >
            <div className="vbb-overline mb-3">Publish</div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => onChange({ published: e.target.checked })}
                data-testid="essay-published-toggle"
                className="w-4 h-4"
              />
              <span className="text-[14px]">
                {form.published
                  ? "Published (appears in RSS)"
                  : "Draft only"}
              </span>
            </label>
            <div className="mt-5 flex items-center justify-between">
              <span
                className="font-mono-tiny text-[#6b6b6b]"
                data-testid="essay-save-state"
              >
                {savedState === "saving" && "Saving…"}
                {savedState === "saved" && "Saved."}
                {savedState === "error" && (
                  <span className="text-[#e34234]">Save failed.</span>
                )}
              </span>
              <button
                type="button"
                onClick={onSave}
                disabled={savedState === "saving" || !form.title.trim()}
                className="vbb-btn disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="essay-save-btn"
              >
                Save
              </button>
            </div>
          </section>

          <section
            className="border border-[#0a0a0a] p-5"
            data-testid="essay-refs-panel"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="vbb-overline">
                References ({form.reference_ids.length})
              </div>
              <Link
                to="/admin/references/new"
                className="font-mono-tiny vbb-link"
                data-testid="essay-add-new-ref-link"
              >
                + New
              </Link>
            </div>
            {references.length === 0 && (
              <p className="text-[13px] text-[#6b6b6b] font-mono-tiny">
                No references yet.{" "}
                <Link to="/admin/references/new" className="vbb-link">
                  Add one
                </Link>
                .
              </p>
            )}
            <ul className="space-y-2 max-h-[400px] overflow-y-auto">
              {references.map((r) => (
                <li key={r.id}>
                  <label className="flex items-start gap-2 cursor-pointer hover:bg-[#fafafa] p-1">
                    <input
                      type="checkbox"
                      checked={form.reference_ids.includes(r.id)}
                      onChange={() => toggleReference(r.id)}
                      data-testid={`essay-ref-checkbox-${r.id}`}
                      className="w-4 h-4 mt-1"
                    />
                    <span className="text-[13px] leading-snug">
                      <span className="font-medium">
                        {r.csl?.title || "(untitled)"}
                      </span>{" "}
                      <span className="font-mono-tiny text-[#6b6b6b]">
                        · {r.csl?.type || "webpage"}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>
    </main>
  );
}
